import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { message, Spin, Card, Typography, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { DynamicForm } from '../../components/DynamicForm';
import { KeyValueInput, KeyValueItem } from '../../components/KeyValueInput';
import { apiService } from '../../services/api';

const { Text } = Typography;

interface ProjectType {
  id: number;
  name: string;
}

interface Project {
  id: number;
  pid: string;
  client?: {
    name: string;
    contactName?: string;
    contactFirstName?: string;
    contactLastName?: string;
  };
  projectTypes?: ProjectType[];
  customProjectType?: string;
}

interface Checklist {
  id: number;
  status: string;
  testEnvironmentDetails: string;
  accessCredentialsInfo: string;
  scopeConfirmation: boolean;
  additionalNotes: string;
  submittedDate: string;
  customRequirements?: KeyValueItem[];
}

export const ProjectChecklistForm: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState<Project | null>(null);
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [customRequirements, setCustomRequirements] = useState<KeyValueItem[]>([]);

  const fetchProject = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getProjectById(projectId!);
      setProject(response.data);
      fetchChecklist(response.data.pid);
    } catch (error) {
      message.error('Failed to load project information');
      navigate('/projects');
    }
    // Note: loading is set to false in fetchChecklist
  }, [projectId, navigate]);

  useEffect(() => {
    // Try to get project from navigation state first, fallback to API fetch
    const projectFromState = location.state?.project;
    if (projectFromState) {
      setProject(projectFromState);
      fetchChecklist(projectFromState.pid);
    } else if (projectId) {
      fetchProject();
    }
  }, [projectId, location.state, fetchProject]);

  const fetchChecklist = async (pid: string) => {
    try {
      const response = await apiService.getOrCreateChecklist(pid) as Checklist;
      setChecklist(response);
      // Set custom requirements if they exist
      if (response.customRequirements) {
        setCustomRequirements(response.customRequirements);
      }
    } catch (error) {
      message.error('Failed to load checklist');
      console.error('Error fetching checklist:', error);
    } finally {
      setLoading(false); // Always set loading to false after checklist fetch
    }
  };

  const handleSubmit = async (formData: any) => {
    try {
      await apiService.submitChecklist({
        pid: project?.pid,
        ...formData,
        customRequirements: customRequirements.filter(item => item.keyName && item.keyValue),
      });
      // Refresh checklist after submission
      if (project?.pid) {
        await fetchChecklist(project.pid);
      }
    } catch (error) {
      throw error; // Re-throw to let DynamicForm handle the error
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading project information...</Text>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <Card>
        <Text type="danger">Project not found</Text>
      </Card>
    );
  }

  return (
    <div>
      <Card style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <Button 
            onClick={() => navigate('/projects')}
            icon={<ArrowLeftOutlined />}
          >
            Back to Projects
          </Button>
        </div>
      </Card>
      <DynamicForm
        projectId={projectId!}
        projectType={project.projectTypes?.[0]?.name || project.customProjectType || 'default'}
        contentType="requirement-checklist"
        onSubmit={handleSubmit}
        submitEndpoint="/requirement-checklists/submit"
        title="Requirements Checklist"
        description={`Project: ${project.client?.name} - ${project.pid}`}
        initialValues={checklist ? {
          testEnvironmentDetails: checklist.testEnvironmentDetails,
          accessCredentialsInfo: checklist.accessCredentialsInfo,
          scopeConfirmation: checklist.scopeConfirmation,
          additionalNotes: checklist.additionalNotes
        } : undefined}
        extraContent={
          <div style={{ marginTop: 16, marginBottom: 16 }}>
            <KeyValueInput
              value={customRequirements}
              onChange={setCustomRequirements}
              disabled={checklist?.status === 'verified'}
              placeholder={{
                key: 'Key (e.g., Username, API URL, Config)',
                value: 'Value',
              }}
            />
          </div>
        }
      />
    </div>
  );
};
